<script setup lang="ts">
import { ref, inject, watch, computed, onMounted, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  LayoutDashboard,
  Calendar,
  Building2,
  User,
  Menu,
  ChevronDown,
  X,
  Check,
  LogOut,
  Shield
} from 'lucide-vue-next'
import { getUserOrgs, getUser } from '@/services/db'
import { signOut } from '@/services/auth'
import type { User as UserType, Organization } from '@/types'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const showOrgSelector = ref(false)
const showUserMenu = ref(false)
const currentUser = ref<UserType | null>(null)
const userOrgs = ref<Organization[]>([])

const activeOrgId = inject<{ value: string | null }>('activeOrgId')
const currentOrg = ref<Organization | null>(null)

const wideMode = ref<boolean | 'dashboard'>(false)
provide('wideMode', wideMode)

const mobileQuery = typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)') : null
const isMobile = ref(mobileQuery?.matches ?? false)
if (mobileQuery) {
  mobileQuery.addEventListener('change', (e) => { isMobile.value = e.matches })
}

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Panel' },
  { path: '/events', icon: Calendar, label: t('nav.events') },
  { path: '/organizations', icon: Building2, label: t('nav.organizations') }
]

const primaryNavItems = computed(() => [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Panel' },
  { path: '/events', icon: Calendar, label: t('nav.events') },
  { path: '/organizations', icon: Building2, label: t('nav.organizations') }
])

const secondaryNavItems = computed(() => [] as { path: string; icon: typeof LayoutDashboard; label: string }[])

const showMoreDrawer = ref(false)

function closeMoreDrawer() {
  showMoreDrawer.value = false
}

function openUserMenuAndCloseDrawer() {
  showMoreDrawer.value = false
  toggleUserMenu()
}

const isActive = (path: string) => route.path.startsWith(path)

const toggleOrgSelector = () => {
  showOrgSelector.value = !showOrgSelector.value
  showUserMenu.value = false
}

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
  showOrgSelector.value = false
}

const handleLogout = async () => {
  await signOut()
  showUserMenu.value = false
  router.push({ name: 'login' })
}

const selectOrg = (orgId: string) => {
  if (activeOrgId) activeOrgId.value = orgId
  currentOrg.value = userOrgs.value.find((o) => o.id === orgId) ?? null
  localStorage.setItem('activeOrgId', orgId)
  showOrgSelector.value = false
}

watch(
  () => activeOrgId?.value,
  (newVal) => {
    if (newVal && userOrgs.value.length > 0) {
      currentOrg.value = userOrgs.value.find((o) => o.id === newVal) ?? null
    }
  }
)

onMounted(async () => {
  const userId = localStorage.getItem('currentUserId')
  if (userId) {
    currentUser.value = (await getUser(userId)) ?? null
    if (currentUser.value) {
      userOrgs.value = await getUserOrgs(currentUser.value.id)
      if (activeOrgId && !activeOrgId.value && userOrgs.value.length > 0) {
        const defaultOrg = localStorage.getItem('activeOrgId')
        const org = defaultOrg
          ? userOrgs.value.find((o) => o.id === defaultOrg)
          : userOrgs.value[0]
        if (org) {
          activeOrgId.value = org.id
          currentOrg.value = org
        }
      } else if (activeOrgId?.value) {
        currentOrg.value = userOrgs.value.find((o) => o.id === activeOrgId.value) ?? null
      }
    }
  }
})
</script>

<template>
  <div class="app-shell" :class="{ 'is-mobile': isMobile }">
    <!-- Desktop: Top Bar -->
    <header v-if="!isMobile" class="top-bar" aria-label="Navegación principal">
      <router-link to="/dashboard" class="top-bar-logo">
        <img src="/teempad_events_logo.svg" alt="Teempad Events" class="logo-img" />
        <span class="logo-text">
          <span class="logo-name">Teempad</span>
          <span class="logo-events"> Events</span>
        </span>
      </router-link>

      <nav class="top-bar-nav">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="top-nav-item"
          :class="{ active: isActive(item.path) }"
        >
          <component :is="item.icon" :size="18" :stroke-width="2" />
          <span>{{ item.label }}</span>
        </router-link>
        <router-link
          v-if="currentUser?.isSuperAdmin"
          to="/admin"
          class="top-nav-item top-nav-admin"
          :class="{ active: isActive('/admin') }"
        >
          <Shield :size="16" :stroke-width="2" />
          <span>Admin</span>
        </router-link>
      </nav>

      <div class="top-bar-actions">
        <button
          v-if="currentOrg"
          class="org-badge"
          @click="toggleOrgSelector"
          aria-label="Cambiar organización"
        >
          <span v-if="currentOrg.logoUrl" class="org-logo">
            <img :src="currentOrg.logoUrl" :alt="currentOrg.name" />
          </span>
          <span v-else class="org-dot" :style="{ backgroundColor: currentOrg.color }"></span>
          <span class="org-name">{{ currentOrg.name }}</span>
          <ChevronDown :size="14" />
        </button>
        <button
          class="user-trigger"
          @click="toggleUserMenu"
          aria-label="Menú de usuario"
        >
          <User :size="20" :stroke-width="2" />
        </button>
      </div>
    </header>

    <!-- User Menu -->
    <Teleport to="body">
      <transition name="fade">
        <div v-if="showUserMenu" class="user-menu-overlay" @click.self="showUserMenu = false">
          <div class="user-menu-popup">
            <div class="user-menu-header">
              <div class="user-avatar">
                <User :size="24" />
              </div>
              <div class="user-info">
                <span class="user-name">{{ currentUser?.displayName || 'Usuario' }}</span>
                <span class="user-email">{{ currentUser?.email }}</span>
              </div>
            </div>
            <div class="user-menu-actions">
              <button class="user-menu-item logout" @click="handleLogout">
                <LogOut :size="16" />
                <span>{{ t('nav.logout') }}</span>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <main class="main-content">
      <div class="main-content-inner" :class="{ wide: wideMode === true, dashboard: wideMode === 'dashboard' }">
        <slot></slot>
      </div>
    </main>

    <!-- Mobile: Bottom nav -->
    <nav v-if="isMobile" class="bottom-nav" aria-label="Navegación principal">
      <router-link
        v-for="item in primaryNavItems"
        :key="item.path"
        :to="item.path"
        class="bottom-nav-item"
        :class="{ active: isActive(item.path) }"
      >
        <component :is="item.icon" :size="24" :stroke-width="1.75" class="bottom-nav-icon" />
        <span class="bottom-nav-label">{{ item.label }}</span>
      </router-link>
      <button
        type="button"
        class="bottom-nav-item bottom-nav-more"
        :class="{ active: showMoreDrawer }"
        aria-label="Más"
        @click="showMoreDrawer = true"
      >
        <Menu :size="24" :stroke-width="1.75" class="bottom-nav-icon" />
        <span class="bottom-nav-label">Más</span>
      </button>
    </nav>

    <!-- Mobile: More drawer -->
    <Teleport to="body">
      <Transition name="drawer">
        <div v-if="isMobile && showMoreDrawer" class="more-drawer-overlay" @click.self="closeMoreDrawer">
          <div class="more-drawer" role="dialog" aria-label="Más opciones">
            <div class="more-drawer-header">
              <h2>Más</h2>
              <button type="button" class="close-btn" aria-label="Cerrar" @click="closeMoreDrawer">
                <X :size="22" />
              </button>
            </div>
            <nav class="more-drawer-nav">
              <router-link
                v-for="item in secondaryNavItems"
                :key="item.path"
                :to="item.path"
                class="more-drawer-item"
                @click="closeMoreDrawer"
              >
                <component :is="item.icon" :size="22" :stroke-width="1.75" />
                <span>{{ item.label }}</span>
              </router-link>
              <button type="button" class="more-drawer-item more-drawer-account" @click="openUserMenuAndCloseDrawer">
                <User :size="22" :stroke-width="1.75" />
                <span>Cuenta y Ajustes</span>
              </button>
            </nav>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Org Selector Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showOrgSelector" class="modal-overlay" @click.self="showOrgSelector = false">
          <div class="modal-content org-selector">
            <div class="modal-header">
              <h2>Seleccionar organización</h2>
              <button class="close-btn" @click="showOrgSelector = false">
                <X :size="20" />
              </button>
            </div>
            <div class="org-list">
              <button
                v-for="org in userOrgs"
                :key="org.id"
                class="org-item"
                :class="{ active: org.id === activeOrgId?.value }"
                @click="selectOrg(org.id)"
              >
                <span v-if="org.logoUrl" class="org-logo">
                  <img :src="org.logoUrl" :alt="org.name" />
                </span>
                <span v-else class="org-color" :style="{ backgroundColor: org.color }"></span>
                <span class="org-name">{{ org.name }}</span>
                <Check :size="16" class="org-check" v-if="org.id === activeOrgId?.value" />
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  height: 100dvh;
}

/* Top bar - desktop */
.top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  z-index: var(--z-sticky);
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-6);
}

.top-bar-logo {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  text-decoration: none;
  color: inherit;
}

.logo-img {
  width: 28px;
  height: 24px;
  object-fit: contain;
}

.logo-text {
  display: flex;
  align-items: baseline;
}

.logo-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--color-text-primary);
}

.logo-events {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.top-bar-nav {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.top-nav-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: 0.8125rem;
  font-weight: 500;
  transition: color var(--transition-fast), background var(--transition-fast);
}

.top-nav-item:hover {
  color: var(--color-text-primary);
  background: var(--color-bg-secondary);
}

.top-nav-item.active {
  color: var(--color-text-primary);
  background: var(--color-bg-secondary);
  font-weight: 600;
}

.top-nav-admin {
  margin-left: var(--space-2);
  border-left: 1px solid var(--color-border);
  padding-left: var(--space-4);
}

.top-nav-admin.active {
  color: var(--color-accent);
}

.top-bar-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.org-badge {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-lg);
  color: var(--color-text-secondary);
  font-size: 0.8125rem;
  cursor: pointer;
  background: var(--color-bg-secondary);
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--transition-fast);
}

.org-badge:hover {
  box-shadow: var(--shadow-md);
}

.org-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.org-logo {
  width: 24px;
  height: 24px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.org-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.org-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  transition: color var(--transition-fast), background var(--transition-fast);
}

.user-trigger:hover {
  color: var(--color-text-primary);
  background: var(--color-bg-tertiary);
}

.main-content {
  flex: 1;
  padding: calc(60px + var(--space-6)) var(--space-5) var(--space-5);
  overflow-y: auto;
}

.main-content-inner {
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  transition: max-width var(--transition-base);
}

.main-content-inner.wide {
  max-width: 1200px;
}

.main-content-inner.dashboard {
  max-width: 1400px;
}


/* Bottom nav - mobile (flotante) */
.bottom-nav {
  position: fixed;
  bottom: calc(var(--space-3) + env(safe-area-inset-bottom, 0px));
  left: var(--space-4);
  right: var(--space-4);
  height: 60px;
  padding: 0 var(--space-4);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-floating);
  display: flex;
  justify-content: space-around;
  align-items: center;
  z-index: var(--z-sticky);
}

.app-shell.is-mobile .main-content {
  padding-top: var(--space-5);
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 64px + var(--space-4) * 2);
}

.bottom-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  padding: var(--space-2);
  color: var(--color-text-muted);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.bottom-nav-item.active {
  color: var(--color-accent);
}

.bottom-nav-label {
  font-size: 0.6875rem;
  font-weight: 500;
}

/* Overlays */
.user-menu-overlay,
.more-drawer-overlay,
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: var(--z-modal);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.user-menu-popup {
  width: 100%;
  max-width: 400px;
  background: var(--color-bg-elevated);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  padding: var(--space-6);
  padding-bottom: calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
}

.user-menu-header {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--color-bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.user-name {
  font-weight: 600;
  color: var(--color-text-primary);
}

.user-email {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}

.user-menu-actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.user-menu-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.875rem;
  text-align: left;
  width: 100%;
  transition: background var(--transition-fast);
}

.user-menu-item:hover {
  background: var(--color-bg-tertiary);
}

.user-menu-item.logout {
  color: var(--color-error);
}

.more-drawer {
  width: 100%;
  max-width: 400px;
  background: var(--color-bg-elevated);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  padding: var(--space-6);
  padding-bottom: calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
}

.more-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-6);
}

.more-drawer-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  padding: var(--space-2);
  color: var(--color-text-muted);
  background: none;
  border: none;
  cursor: pointer;
  border-radius: var(--radius-sm);
}

.close-btn:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
}

.more-drawer-nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.more-drawer-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  text-decoration: none;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.875rem;
  text-align: left;
  width: 100%;
  transition: background var(--transition-fast);
}

.more-drawer-item:hover {
  background: var(--color-bg-tertiary);
}

.modal-content {
  background: var(--color-bg-elevated);
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  max-width: 400px;
  width: 100%;
  margin: var(--space-4);
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-4);
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 600;
}

.org-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.org-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-bg-elevated);
  color: var(--color-text-primary);
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: all var(--transition-fast);
  box-shadow: var(--shadow-sm);
}

.org-item:hover {
  box-shadow: var(--shadow-md);
}

.org-item.active {
  background: var(--color-bg-tertiary);
  box-shadow: var(--shadow-sm);
}

.org-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.org-item .org-logo {
  width: 32px;
  height: 32px;
}

.org-check {
  margin-left: auto;
  color: var(--color-text-primary);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.2s ease;
}

.drawer-enter-active .more-drawer,
.drawer-leave-active .more-drawer {
  transition: transform 0.3s ease;
}

.drawer-enter-from .more-drawer,
.drawer-leave-to .more-drawer {
  transform: translateY(100%);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  opacity: 0;
  transform: scale(0.98);
}
</style>
